import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import type { FlagsStateContainer, FlagDescriptor } from '@confluence/flags';
import { Cohorts, COHORT_KEY } from '@confluence/onboarding-helpers/entry-points/constants/cohorts';

import { CopyLinkFlagComponent } from '../components/CopyLinkFlagComponent';

// We want the flag to stay for about 8000ms + view page load time 3000ms (according to FMP)
export const PUBLISH_SUCCESS_FLAG_AUTO_CLOSE_TIME = 11000;

type ShowPublishSuccessFlagsProps = {
	isFirstTimePublish: boolean;
	shouldTimeout?: boolean;
	spaceName?: string;
	flags: FlagsStateContainer;
	title: string | null;
	intl: IntlShape;
	siteTitle?: string;
	isCompanyHub?: boolean;
	copyContentLink?: () => void;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
};

const i18n = defineMessages({
	publishSuccessTitle: {
		id: 'editor-features.success.publish.title',
		description: 'Success title message when a page is published',
		defaultMessage: 'Published!',
	},
	updateSuccessTitle: {
		id: 'editor-features.success.update.title',
		description: 'Success title message when a page is updated',
		defaultMessage: 'Updated!',
	},
	publishSuccessMessageWithSpaceName: {
		id: 'editor-features.user.publish.success.with.space.name',
		description: 'Success message containing space name when publishing a page',
		defaultMessage: '{pageTitle} was successfully published in the {spaceName} space',
	},
	updateSuccessMessageWithSpaceName: {
		id: 'editor-features.user.update.success.with.space.name',
		description: 'Success message containing space name when updating a page',
		defaultMessage: '{pageTitle} was successfully updated in the {spaceName} space',
	},
	publishSuccessMessage: {
		id: 'editor-features.user.publish.success',
		description: 'Success message when publishing a page',
		defaultMessage: '{pageTitle} was successfully published',
	},
	updateSuccessMessage: {
		id: 'editor-features.user.update.success',
		description: 'Success message when updating a page',
		defaultMessage: '{pageTitle} was successfully updated',
	},
});

export const COPY_LINK_IN_UPDATE_FLAG_EXPERIMENT_PAID = 'cc_onboarding_copy_link_in_update_flag';
export const COPY_LINK_IN_UPDATE_FLAG_EXPERIMENT_FREE =
	'cc_onboarding_copy_link_in_update_flag_free_exp';

const shouldShowCopyLinkInUpdateFlag = (): boolean => {
	// Evaluate cohort for Paid and Free experiments
	const paidExperimentCohort = FeatureGates.getExperimentValue<Cohorts>(
		COPY_LINK_IN_UPDATE_FLAG_EXPERIMENT_PAID,
		COHORT_KEY,
		Cohorts.NOT_ENROLLED,
		{ fireExperimentExposure: false },
	);
	const freeExperimentCohort = FeatureGates.getExperimentValue<Cohorts>(
		COPY_LINK_IN_UPDATE_FLAG_EXPERIMENT_FREE,
		COHORT_KEY,
		Cohorts.NOT_ENROLLED,
		{ fireExperimentExposure: false },
	);

	// Based on cohorts, fire exposure event if enrolled
	if (paidExperimentCohort !== Cohorts.NOT_ENROLLED) {
		FeatureGates.manuallyLogExperimentExposure(COPY_LINK_IN_UPDATE_FLAG_EXPERIMENT_PAID);
	} else if (freeExperimentCohort !== Cohorts.NOT_ENROLLED) {
		FeatureGates.manuallyLogExperimentExposure(COPY_LINK_IN_UPDATE_FLAG_EXPERIMENT_FREE);
	}

	// Show the copy link if enrolled in either experiment
	return paidExperimentCohort === Cohorts.EXPERIMENT || freeExperimentCohort === Cohorts.EXPERIMENT;
};

const generateSpaceNameFlagMessage = (
	flagMessageData: {
		date?: string;
		time?: string;
		spaceName?: string;
		pageTitle?: React.ReactNode;
	},
	i18nSuccessMsg: MessageDescriptor,
	i18nSpaceNameMsg?: MessageDescriptor,
) => {
	const { spaceName } = flagMessageData;
	const i18nMsg = spaceName ? i18nSpaceNameMsg : i18nSuccessMsg;
	return <FormattedMessage {...i18nMsg} values={{ ...flagMessageData }} />;
};

export const showPublishSuccessFlags = async ({
	isFirstTimePublish,
	shouldTimeout = true,
	spaceName,
	flags,
	title,
	intl,
	copyContentLink,
	createAnalyticsEvent,
}: ShowPublishSuccessFlagsProps) => {
	const successPublishTitle = isFirstTimePublish
		? i18n.publishSuccessTitle
		: i18n.updateSuccessTitle;

	const successPublishMessage = generateSpaceNameFlagMessage(
		{ spaceName, pageTitle: <b>{title}</b> },
		isFirstTimePublish ? i18n.publishSuccessMessage : i18n.updateSuccessMessage,
		isFirstTimePublish
			? i18n.publishSuccessMessageWithSpaceName
			: i18n.updateSuccessMessageWithSpaceName,
	);

	let flagProps: Partial<FlagDescriptor> = {};

	if (
		copyContentLink &&
		createAnalyticsEvent &&
		(isFirstTimePublish || shouldShowCopyLinkInUpdateFlag())
	) {
		flagProps = {
			description: (
				<CopyLinkFlagComponent
					copyContentLink={copyContentLink}
					successPublishMessage={successPublishMessage}
					createAnalyticsEvent={createAnalyticsEvent}
					isFirstTimePublish={isFirstTimePublish}
				/>
			),
		};
	}

	const publishSuccessFlag = await flags.showFlag({
		type: 'success-circle',
		title: intl.formatMessage(successPublishTitle),
		description: successPublishMessage,
		close: 'never',
		messageId: 'confluence-frontend-editor-features-publish-success-flag',
		messageType: 'transactional',
		...flagProps,
	});
	if (shouldTimeout) {
		setTimeout(() => flags.hideFlag(publishSuccessFlag.id), PUBLISH_SUCCESS_FLAG_AUTO_CLOSE_TIME);
	}
};
